import {
  createClient,
  ClientEvent,
  RoomEvent,
  MatrixEventEvent,
} from "matrix-js-sdk";
import {
  CryptoEvent,
  VerifierEvent,
  VerificationPhase,
  VerificationRequestEvent,
} from "matrix-js-sdk/lib/crypto-api";
import { decodeRecoveryKey } from "matrix-js-sdk/lib/crypto-api/recovery-key";
import { SERVER } from "./server";

/*
  Der Zugang zum Chat.

  Der Chat läuft nicht über PocketBase, sondern über Matrix — ein offenes
  Chat-Verfahren mit Ende-zu-Ende-Verschlüsselung. „Ende zu Ende“ heißt: Die
  Beiträge werden auf dem Gerät verschlüsselt und erst auf dem Gerät des
  Empfängers wieder lesbar. Unser eigener Server kann sie nicht lesen.

  ---------------------------------------------------------------
  Niemand meldet sich zweimal an
  ---------------------------------------------------------------

  Ein Mitglied kennt nur sein Passwort für das Fraktionsbüro. Das
  Matrix-Konto stellt der Server: Die App fragt mit dem vorhandenen
  Anmelde-Token bei `/api/fb/matrix-anmeldung` nach und bekommt Zugang
  zurück. Das Matrix-Passwort sieht sie nie.

  ---------------------------------------------------------------
  Warum die Gerätekennung gespeichert wird
  ---------------------------------------------------------------

  Bei Matrix hängen die Schlüssel am **Gerät**, nicht am Konto. Ein neues
  Gerät kann alte Beiträge nicht entschlüsseln. Würde bei jedem Neuladen der
  Seite ein neues entstehen, wäre der Verlauf nach dem zweiten Öffnen
  unlesbar — und die Geräteliste liefe voll.

  Deshalb merkt sich der Browser seine Gerätekennung und meldet sich damit
  wieder an. Das **Zugangstoken** wird bewusst NICHT gespeichert: Es holt
  sich die App bei jedem Start neu. Ein Token im Speicher des Browsers wäre
  ein Schlüssel, der herumliegt.

  Die Schlüssel selbst liegen in der IndexedDB des Browsers — dort legt die
  Matrix-Bibliothek sie ab, verwaltet von ihrem eigenen Verschlüsselungsteil.
*/

const GERAET_SCHLUESSEL = "fraktionsbuero-matrix-geraet";

/* Eigene Felder an einer Nachricht. Der Punkt-Aufbau ist bei Matrix üblich. */
const FELD_TOP = "de.fraktionsbuero.top";
const FELD_NOTIZ = "de.fraktionsbuero.notiz";
const FELD_AUFGABE = "de.fraktionsbuero.aufgabe";

function gemerktesGeraet() {
  try {
    return window.localStorage.getItem(GERAET_SCHLUESSEL) || null;
  } catch {
    return null;
  }
}

function merkeGeraet(geraet) {
  try {
    if (geraet) window.localStorage.setItem(GERAET_SCHLUESSEL, geraet);
  } catch {
    /* Privater Modus — dann eben jedes Mal ein neues Gerät. */
  }
}

export function vergissGeraet() {
  try {
    window.localStorage.removeItem(GERAET_SCHLUESSEL);
  } catch {
    /* nichts zu tun */
  }
}

/* ---------------------------------------------------------------
   Aufrufe an unseren eigenen Server
   --------------------------------------------------------------- */

async function anUnserenServer(pfad, token, koerper) {
  /*
    Mit Server-Vorsatz: Beim Entwickeln läuft die App auf dem eigenen Rechner
    und der Server woanders. Ohne den Vorsatz fragte der Browser den
    Entwicklungsserver — und der kennt diese Wege nicht.
  */
  const antwort = await fetch(`${SERVER}${pfad}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: token },
    body: JSON.stringify(koerper || {}),
  });

  const daten = await antwort.json().catch(() => ({}));
  if (!antwort.ok) {
    throw new Error(daten.fehler || "Der Chat ist gerade nicht erreichbar.");
  }
  return daten;
}

export function holeZugang(token) {
  return anUnserenServer("/api/fb/matrix-anmeldung", token, {
    geraet: gemerktesGeraet() || "",
  });
}

export function stelleRaeumeSicher(token, kanaele) {
  /*
    Die eigene Gerätekennung geht mit. Sonst meldet der Server sich für diesen
    Aufruf noch einmal an und legt dabei ein zweites Gerät an — bei jedem
    Start der App eines mehr.
  */
  return anUnserenServer("/api/fb/matrix-raeume", token, {
    kanaele,
    geraet: gemerktesGeraet() || "",
  });
}

/*
  Die eigenen Matrix-Zugangsdaten — für eine andere Chat-App auf dem Handy.

  Das Fraktionsbüro braucht sie nicht; es holt sich seinen Zugang selbst. Wer
  aber Element oder eine andere Matrix-App benutzen will, muss sich dort von
  Hand anmelden können.
*/
export function holeZugangsdaten(token) {
  return anUnserenServer("/api/fb/matrix-zugangsdaten", token, {});
}

/* ---------------------------------------------------------------
   Die Verbindung selbst
   --------------------------------------------------------------- */

/*
  Startet den Chat und meldet jede Änderung über `aufAenderung`.

  Gibt ein Objekt zurück, mit dem man senden und wieder aufräumen kann.
  Alles Weitere läuft über Rückrufe — die Oberfläche fragt nie nach, sie
  wird benachrichtigt.
*/
export async function verbinde({
  zugang,
  zugangsdaten,
  raeume,
  aufAenderung,
  aufZustand,
  aufBestaetigungsanfrage,
}) {
  merkeGeraet(zugang.geraet);

  /*
    Der Schlüssel der Schlüsselsicherung, solange diese Sitzung läuft.

    Die Bibliothek legt ihn beim Einrichten an, gibt ihn uns — und fragt im
    selben Zug wieder danach, weil sie ihn zum Ablegen der Sicherung braucht.
    Ohne eine Stelle, an der sie ihn abholen kann, bricht sie mit
    „No getSecretStorageKey callback supplied“ ab. Genau daran ist der erste
    Versuch gescheitert.

    Er liegt bewusst nur im Arbeitsspeicher: Beim nächsten Start wird er
    nicht mehr gebraucht, und im Browserspeicher wäre er ein Schlüssel, der
    neben dem Schloss hängt.
  */
  let sicherungsschluessel = null;

  const klient = createClient({
    baseUrl: zugang.server,
    accessToken: zugang.token,
    userId: zugang.kennung,
    deviceId: zugang.geraet,
    /*
      Zeitüberschreitungen beim Warten auf Neues sind normal und kein Fehler
      — ohne das schreibt die Bibliothek die Konsole voll.
    */
    timelineSupport: true,

    cryptoCallbacks: {
      /* „Gib mir den Schlüssel zur Schlüsselsicherung.“ */
      getSecretStorageKey: async ({ keys }) => {
        if (!sicherungsschluessel) return null;

        const kennungen = Object.keys(keys || {});
        if (!kennungen.length) return null;

        /*
          Passt eine der genannten Kennungen zu unserem Schlüssel, nehmen wir
          die. Beim allerersten Einrichten hat er noch keine — dann ist die
          einzige genannte gemeint.
        */
        const treffer = kennungen.includes(sicherungsschluessel.kennung)
          ? sicherungsschluessel.kennung
          : kennungen[0];

        return [treffer, sicherungsschluessel.schluessel];
      },

      /* „Merk dir diesen Schlüssel für gleich.“ */
      cacheSecretStorageKey: (kennung, _angaben, schluessel) => {
        sicherungsschluessel = { kennung, schluessel };
      },
    },
  });

  /*
    Die Verschlüsselung. Sie ist kein JavaScript, sondern ein
    WebAssembly-Modul — ausführbarer Code, der im Browser läuft. Ohne diesen
    Aufruf sendet die Bibliothek unverschlüsselt, und der Server könnte
    mitlesen.
  */
  await klient.initRustCrypto();

  /*
    Geräteprüfung und Schlüsselsicherung einrichten.

    Ohne beides ist die Verschlüsselung zwar da, aber gefährlich:

    - Ohne **Geräteprüfung** (Matrix nennt es Cross-Signing) gilt jedes Gerät
      als unbestätigt. In fremden Matrix-Apps steht dann an jedem Beitrag
      eine Warnung, und niemand kann unterscheiden, ob ein neues Gerät
      wirklich dem Kollegen gehört.
    - Ohne **Schlüsselsicherung** verliert man mit dem Browserspeicher den
      gesamten Verlauf. Endgültig — der Server kann nicht helfen, er kennt
      die Beiträge nicht.

    Beides verlangt bei Matrix normalerweise das Passwort. Unsere Mitglieder
    kennen es nicht, deshalb steht auf dem Server `ui_auth.session_timeout`:
    Wer sich gerade angemeldet hat, gilt als bestätigt.

    Der Wiederherstellungsschlüssel entsteht nur beim allerersten Mal. Er
    wird zurückgegeben, damit die Oberfläche ihn einmal zeigen kann — und
    nirgends gespeichert.
  */
  let wiederherstellung = null;
  let kryptofehler = null;

  /*
    Räume, in die man eingeladen wurde, werden von selbst betreten. Für eine
    Fraktion ist das richtig: Die Einladung kommt vom eigenen Server, und
    niemand soll morgens erst fünf Einladungen wegklicken.
  */
  klient.on(RoomEvent.MyMembership, async (raum, stand) => {
    if (stand === "invite") {
      try {
        await klient.joinRoom(raum.roomId);
      } catch (fehler) {
        /* Beim nächsten Start noch einmal. */
      }
    }
  });

  klient.on(ClientEvent.Sync, (stand) => {
    if (stand === "PREPARED" || stand === "SYNCING") aufZustand("bereit");
    if (stand === "ERROR") aufZustand("fehler");
    if (stand === "PREPARED") aufAenderung();
  });

  klient.on(RoomEvent.Timeline, () => aufAenderung());

  /* Ein Beitrag, der erst später entschlüsselt werden konnte. */
  klient.on(MatrixEventEvent.Decrypted, () => aufAenderung());

  /*
    Das Gegenstück zur Freischaltung: Auf diesem Gerät kommt die Anfrage
    eines anderen an.

    Ohne diesen Zuhörer liefe das Bestätigen ins Leere — das neue Gerät
    fragte, und niemand antwortete. Beide Seiten müssen dieselben Bilder
    sehen und beide zustimmen; nur dann ist ausgeschlossen, dass jemand
    dazwischensteht.
  */
  klient.on(CryptoEvent.VerificationRequestReceived, async (anfrage) => {
    if (!aufBestaetigungsanfrage) return;

    aufBestaetigungsanfrage({
      geraet: anfrage.otherDeviceId || "unbekanntes Gerät",

      async annehmen(rueckrufe) {
        await anfrage.accept();
        await fuehreBildvergleich(anfrage, rueckrufe);
      },

      ablehnen() {
        anfrage.cancel();
      },
    });
  });

  /*
    Verbinden und erst DANACH die Verschlüsselung einrichten.

    Die Reihenfolge ist nicht beliebig: `resetEncryption` und das Anlegen der
    Schlüsselsicherung brauchen eine laufende Verbindung. Vorher aufgerufen
    warten sie stumm auf einen Abgleich, der noch gar nicht läuft — die App
    blieb dann für immer bei „wird verbunden“ stehen, ohne Fehlermeldung.
  */
  await klient.startClient({ initialSyncLimit: 100 });

  /* Auf den ersten Abgleich warten, aber nicht ewig. */
  await new Promise((fertig) => {
    let erledigt = false;
    const schliesse = () => {
      if (erledigt) return;
      erledigt = true;
      fertig();
    };
    const zeit = setTimeout(schliesse, 25000);
    klient.on(ClientEvent.Sync, (stand) => {
      if (stand === "PREPARED" || stand === "SYNCING") {
        clearTimeout(zeit);
        schliesse();
      }
    });
  });

  /*
    Den Räumen der Fraktion ausdrücklich beitreten.

    Auf die Benachrichtigung „du wurdest eingeladen“ ist kein Verlass: Sie
    kann vor dem Anmelden der Zuhörer eintreffen oder im ersten Abgleich
    untergehen. Dann ist man eingeladen, aber nicht drin — und jeder Beitrag
    scheitert mit „403, nicht im Raum“.

    Beitreten ist harmlos, wenn man schon drin ist.
  */
  for (const raumId of Object.values(raeume || {})) {
    const raum = klient.getRoom(raumId);
    if (raum && raum.getMyMembership() === "join") continue;
    try {
      await klient.joinRoom(raumId);
    } catch (fehler) {
      /* Beim nächsten Start noch einmal. */
    }
  }

  /*
    Die Passwortabfrage beantworten.

    Matrix schützt heikle Schritte — Signaturschlüssel hochladen, die
    Verschlüsselung zurücksetzen — mit einer zweiten Bestätigung. Unsere
    Mitglieder kennen ihr Matrix-Passwort nicht, aber der Server verwahrt es
    und gibt es dem eigenen Konto heraus.

    Erst wird es ohne Passwort versucht: Kurz nach der Anmeldung lässt der
    Server es durchgehen. Danach läuft diese Nachsicht ab — sich darauf zu
    verlassen war einer der Fehler, an denen die Einrichtung scheiterte.
  */
  async function bestaetige(anfrage) {
    try {
      return await anfrage(null);
    } catch (fehler) {
      const sitzung = fehler && fehler.data && fehler.data.session;
      if (!sitzung || !zugangsdaten) throw fehler;

      return await anfrage({
        type: "m.login.password",
        identifier: { type: "m.id.user", user: zugangsdaten.kennung },
        password: zugangsdaten.passwort,
        session: sitzung,
      });
    }
  }

  let freischaltungNoetig = false;

  try {
    const krypto = klient.getCrypto();

    /*
      Drei Fälle, und sie sauber zu trennen ist das Wichtigste in dieser
      Datei. Wird einer falsch erkannt, setzt die App im schlimmsten Fall die
      Verschlüsselung zurück — und macht damit alte Beiträge für immer
      unlesbar.

      Zwei Fragen genügen:

        Gibt es auf dem SERVER eine Schlüsselsicherung?
        Liegen die Signaturschlüssel auf DIESEM Gerät?

      ── keine Sicherung ───────────────────────────────────────────
      Erstes Gerät. Es kann nichts verloren gehen, denn es wurde noch nie
      etwas gesichert. Alles frisch aufsetzen und den
      Wiederherstellungsschlüssel zeigen. Das räumt auch halbfertige Zustände
      weg, in denen eine Ablage ohne passenden Schlüssel zurückblieb.

      ── Sicherung da, Schlüssel hier ──────────────────────────────
      Alles in Ordnung. NICHTS anfassen. Ein `bootstrapCrossSigning` an
      dieser Stelle würde die Signaturschlüssel erneut in die Ablage
      schreiben wollen und nach deren Schlüssel fragen — den kennt nur das
      Mitglied, auf seinem Zettel.

      ── Sicherung da, Schlüssel fehlen hier ───────────────────────
      Ein neues Gerät. Auch hier NICHTS anfassen: Es muss erst freigeschaltet
      werden, entweder durch Bestätigen an einem laufenden Gerät oder durch
      Eingabe des Wiederherstellungsschlüssels. Bis dahin kann man neue
      Beiträge schreiben und lesen, ältere nicht.
    */
    const sicherung = await krypto.getKeyBackupInfo();
    const bereit = await krypto.isCrossSigningReady();

    if (!sicherung) {
      await krypto.resetEncryption(bestaetige);

      await krypto.bootstrapSecretStorage({
        setupNewSecretStorage: true,
        /* `resetEncryption` hat die Sicherung bereits angelegt. */
        setupNewKeyBackup: false,
        createSecretStorageKey: async () => {
          const schluessel = await krypto.createRecoveryKeyFromPassphrase();

          /*
            Sofort hinterlegen: Die Bibliothek fragt noch während dieses
            Aufrufs danach.
          */
          sicherungsschluessel = {
            kennung: null,
            schluessel: schluessel.privateKey,
          };
          wiederherstellung = schluessel.encodedPrivateKey || null;

          return schluessel;
        },
      });
    } else if (!bereit) {
      /*
        Ein neues Gerät. Es fehlt ihm nichts, was der Server ihm geben
        könnte — die Schlüssel liegen auf den anderen Geräten und hinter dem
        Wiederherstellungsschlüssel. Also wird hier NICHTS versucht, sondern
        gefragt.

        Genau hier stand vorher ein `bootstrapCrossSigning`, das nach dem
        Schlüssel verlangte, den niemand hatte — daher die Fehlermeldung auf
        jedem zweiten Gerät.
      */
      freischaltungNoetig = true;
    }
  } catch (fehler) {
    /*
      Der Chat läuft auch ohne Sicherung weiter — eine App, die sich wegen
      der Schlüsselverwaltung nicht öffnen lässt, wäre schlimmer.

      Aber der Fehler wird NICHT mehr verschluckt. Beim ersten Versuch tat er
      genau das: Die Sicherung kam nicht zustande, niemand erfuhr davon, und
      der Verlust wäre erst aufgefallen, wenn jemand sein Gerät verliert.
    */
    kryptofehler = fehler && fehler.message ? fehler.message : String(fehler);
    console.error("Schlüsselsicherung fehlgeschlagen:", fehler);
  }

  return {
    klient,

    /*
      Nur beim allerersten Einrichten gesetzt. Danach null — der Schlüssel
      wird nicht aufbewahrt, sonst wäre er keine Sicherung, sondern eine
      zweite Kopie an derselben Stelle.
    */
    wiederherstellung,

    /* Gesetzt, wenn die Schlüsselsicherung nicht zustande kam. */
    kryptofehler,

    /*
      Dieses Gerät ist neu und muss freigeschaltet werden. Kein Fehler —
      der Chat läuft, nur ältere Beiträge bleiben verschlossen.
    */
    freischaltungNoetig,

    /* ---------------------------------------------------------------
       Ein neues Gerät freischalten
       --------------------------------------------------------------- */

    /*
      Weg 1: Bestätigen an einem Gerät, das schon läuft.

      Beide Geräte bekommen dieselben sieben Bilder gezeigt. Stimmen sie
      überein, hat niemand dazwischengefunkt — das ist derselbe Gedanke wie
      beim QR-Code von WhatsApp Web, nur zum Anschauen statt zum Scannen.

      `aufBilder` wird gerufen, sobald die Bilder feststehen. Bestätigt wird
      erst, wenn der Mensch zugestimmt hat.
    */
    async bestaetigungAnfordern({ aufBilder, aufFertig, aufAbbruch }) {
      const krypto = klient.getCrypto();
      const anfrage = await krypto.requestOwnUserVerification();
      await fuehreBildvergleich(anfrage, { aufBilder, aufFertig, aufAbbruch });
    },

    /*
      Weg 2: Den Wiederherstellungsschlüssel eintippen.

      Der Schlüssel wird entziffert und in dieselbe Variable gelegt, aus der
      der Rückruf `getSecretStorageKey` sich bedient. Danach kann die
      Bibliothek die Signaturschlüssel aus der Ablage holen.

      Bei einem Tippfehler wird NICHTS zurückgesetzt — sonst verlöre jemand
      seinen Verlauf, weil er sich vertippt hat.
    */
    async mitSchluesselFreischalten(eingabe) {
      const krypto = klient.getCrypto();

      let roh;
      try {
        roh = decodeRecoveryKey(String(eingabe || "").trim());
      } catch (fehler) {
        throw new Error(
          "Das sieht nicht nach einem Wiederherstellungsschlüssel aus. " +
            "Er besteht aus zwölf Vierergruppen."
        );
      }

      sicherungsschluessel = { kennung: null, schluessel: roh };

      try {
        await krypto.bootstrapCrossSigning({ setupNewCrossSigning: false });
      } catch (fehler) {
        sicherungsschluessel = null;
        throw new Error(
          "Mit diesem Schlüssel ließ sich nichts öffnen. Prüf bitte die " +
            "Schreibweise — es ist nicht dein Passwort, sondern der lange " +
            "Schlüssel vom Zettel."
        );
      }
    },

    /*
      Nach dem Freischalten: den Verlauf aus der Sicherung holen.

      Das kann bei langen Verläufen dauern, deshalb der Fortschritt.
    */
    /*
      Die eigenen Geräte.

      Das ist die Stelle, an der auffällt, wenn sich jemand als man selbst
      angemeldet hat — auch unser eigener Server, der das Matrix-Passwort
      kennt. Ein Gerät, das man nicht kennt, gehört abgemeldet.
    */
    async meineGeraete() {
      const krypto = klient.getCrypto();
      const eigene = klient.getUserId();

      const geraete = await klient.getDevices();
      const zustand = await krypto.getUserDeviceInfo([eigene]);
      const meine = zustand.get(eigene) || new Map();

      return (geraete.devices || []).map((g) => {
        const angaben = meine.get(g.device_id);
        return {
          id: g.device_id,
          name: g.display_name || "Ohne Namen",
          zuletzt: g.last_seen_ts ? new Date(g.last_seen_ts) : null,
          bestaetigt: Boolean(angaben && angaben.verified),
          dieses: g.device_id === klient.getDeviceId(),
        };
      });
    },

    async geraetAbmelden(geraetId) {
      await klient.deleteDevice(geraetId);
    },

    async verlaufHolen(aufFortschritt) {
      const krypto = klient.getCrypto();
      await krypto.loadSessionBackupPrivateKeyFromSecretStorage();
      const ergebnis = await krypto.restoreKeyBackup({
        progressCallback: (stand) => {
          if (aufFortschritt && stand && stand.stage === "load_keys") {
            aufFortschritt(stand.successes || 0, stand.total || 0);
          }
        },
      });
      aufAenderung();
      return ergebnis;
    },

    /* Beiträge aus allen Räumen, in der Form, die die Oberfläche erwartet. */
    nachrichten(raumZuKanal) {
      const alle = [];

      Object.entries(raumZuKanal).forEach(([kanalId, raumId]) => {
        const raum = klient.getRoom(raumId);
        if (!raum) return;

        raum
          .getLiveTimeline()
          .getEvents()
          .forEach((ereignis) => {
            if (ereignis.getType() !== "m.room.message") return;
            if (ereignis.isRedacted()) return;

            const inhalt = ereignis.getContent() || {};
            const gescheitert = ereignis.isDecryptionFailure();

            alle.push({
              id: ereignis.getId(),
              kanalId,
              /* Die Matrix-Kennung trägt unsere Kontokennung als Ortsteil. */
              verfasserId: ausKennung(ereignis.getSender()),
              zeit: new Date(ereignis.getTs()),
              text: gescheitert
                ? "Dieser Beitrag lässt sich auf diesem Gerät nicht entschlüsseln."
                : inhalt.body || "",
              topId: inhalt[FELD_TOP] || null,
              annotationId: inhalt[FELD_NOTIZ] || null,
              aufgabeId: inhalt[FELD_AUFGABE] || null,
              unlesbar: gescheitert,
            });
          });
      });

      return alle.sort((a, b) => a.zeit - b.zeit);
    },

    async sende(raumId, { text, topId, annotationId, aufgabeId }) {
      const inhalt = { msgtype: "m.text", body: text };
      if (topId) inhalt[FELD_TOP] = topId;
      if (annotationId) inhalt[FELD_NOTIZ] = annotationId;
      if (aufgabeId) inhalt[FELD_AUFGABE] = aufgabeId;
      await klient.sendEvent(raumId, "m.room.message", inhalt);
    },

    beende() {
      klient.stopClient();
    },
  };
}

/*
  Auf einen Zustandswechsel der Anfrage warten.

  Eine Bestätigung läuft über mehrere Schritte: gefragt — bereit — begonnen —
  fertig oder abgebrochen. Die Bibliothek meldet jeden Wechsel über ein
  Ereignis; abgefragt wird der Stand über `phase`.

  Erfundene Bequemlichkeiten wie ein „waitFor“ gibt es hier nicht — das war
  eine Annahme, die beim ersten Klick gescheitert wäre.
*/
function warteAufPhase(anfrage, phasen) {
  return new Promise((fertig) => {
    const pruefe = () => {
      if (phasen.includes(anfrage.phase)) {
        anfrage.off(VerificationRequestEvent.Change, pruefe);
        fertig(anfrage.phase);
      }
    };
    anfrage.on(VerificationRequestEvent.Change, pruefe);
    pruefe();
  });
}

/*
  Den ganzen Bildvergleich führen — von beiden Seiten aus derselbe Ablauf.

  Sieben Bilder erscheinen auf beiden Geräten. Erst wenn ein Mensch bestätigt
  hat, dass sie übereinstimmen, gilt das Gerät als echt. Wer blind tippt,
  hebt den Schutz auf — deshalb steht in der Oberfläche die Frage und nicht
  nur ein Knopf.
*/
async function fuehreBildvergleich(anfrage, { aufBilder, aufFertig, aufAbbruch }) {
  /* Warten, bis die Gegenseite angenommen hat. */
  const stand = await warteAufPhase(anfrage, [
    VerificationPhase.Ready,
    VerificationPhase.Started,
    VerificationPhase.Cancelled,
  ]);

  if (stand === VerificationPhase.Cancelled) {
    aufAbbruch("Die Anfrage wurde am anderen Gerät abgelehnt.");
    return;
  }

  const pruefer =
    anfrage.verifier || (await anfrage.startVerification("m.sas.v1"));

  pruefer.on(VerifierEvent.ShowSas, (sas) => {
    aufBilder({
      bilder: (sas.sas && sas.sas.emoji) || [],
      stimmtUeberein: () => sas.confirm(),
      stimmtNicht: () => sas.mismatch(),
    });
  });

  try {
    /*
      `verify()` läuft, bis beide Seiten bestätigt haben — oder bricht ab.
      Es gibt kein eigenes „fertig“-Ereignis; das Ende ist die Auflösung
      dieses Versprechens.
    */
    await pruefer.verify();
    aufFertig();
  } catch (fehler) {
    aufAbbruch(
      "Die Bestätigung wurde abgebrochen." +
        (fehler && fehler.message ? " " + fehler.message : "")
    );
  }
}

/* „@6hfaodxcnex6ve8:server“ → „6hfaodxcnex6ve8“ */
export function ausKennung(mxid) {
  if (!mxid) return "";
  return String(mxid).replace(/^@/, "").split(":")[0];
}
